import React from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import {
  pageVariants as basePageVariants,
  itemVariants as baseItemVariants,
} from "@/components/memberships/membershipsPageConfig";
import { Loader2 } from "lucide-react";
import RegistrationToast from "../components/memberships/RegistrationToast";
import MembershipPlanGrid from "../components/memberships/MembershipPlanGrid";
import MembershipPageHeader from "../components/memberships/MembershipPageHeader";
import MembershipPageFooter from "../components/memberships/MembershipPageFooter";
import { useMembershipsData } from "../hooks/useMembershipData";
import { usePlanSelection } from "../hooks/usePlanSelection";
import ConfirmationModal from "../components/Modal/ConfirmationModal";
import WarningModal from "../components/Modal/WarningModal";

const MembershipsPage = () => {
  const location = useLocation();
  const dispatch = useDispatch();

  const {
    isAuthenticated,
    user,
    isLoading: isAuthLoading,
    currentSubscription,
  } = useSelector((state) => state.auth);

  const { isPlanProcessing } = useSelector((state) => state.membership);
  const { plans, isMembershipLoading, trialEligible } = useMembershipsData();

  const {
    selectedPlanId,
    handlePlanSelection,
    handleLearnMore,
    isDowngradeConfirmModalOpen,
    setIsDowngradeConfirmModalOpen,
    pendingDowngradePlan,
    handleDowngrade,
    isWarningModalOpen,
    warningModalData,
    handleWarningModalClose,
  } = usePlanSelection(plans);

  const fromRegistration = location.state?.fromRegistration;

  const getCurrentPlanName = () => {
    const userPlan = user?.membership?.plan || "free";
    const plan = plans?.find((p) => p.id === userPlan);
    return plan?.name || userPlan?.toUpperCase() || "Current Plan";
  };

  const getPendingPlanName = () => {
    return (
      pendingDowngradePlan?.name ||
      pendingDowngradePlan?.id?.toUpperCase() ||
      "Selected Plan"
    );
  };

  if (isAuthLoading || isMembershipLoading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <>
      <RegistrationToast
        isAuthLoading={isAuthLoading}
        isAuthenticated={isAuthenticated}
        user={user}
        currentSubscription={currentSubscription}
        fromRegistration={fromRegistration}
      />

      <ConfirmationModal
        isOpen={isDowngradeConfirmModalOpen}
        onClose={() => setIsDowngradeConfirmModalOpen(false)}
        onConfirm={handleDowngrade}
        title="Downgrade Plan"
        message={`Are you sure you want to downgrade from ${getCurrentPlanName()} to ${getPendingPlanName()}? This action cannot be undone.`}
        confirmText="Downgrade"
        cancelText="Cancel"
        loading={isPlanProcessing}
      />

      <WarningModal
        isOpen={isWarningModalOpen}
        onClose={handleWarningModalClose}
        title={warningModalData.title}
        message={warningModalData.message}
        cancelText="Okay"
      />

      <motion.div
        className="space-y-12 w-full bg-gradient-to-br from-background via-secondary/5 to-background"
        initial="hidden"
        animate="visible"
        variants={basePageVariants}
      >
        <MembershipPageHeader
          itemVariants={baseItemVariants}
          trialEligible={trialEligible}
        />

        <MembershipPlanGrid
          plans={plans}
          onSelectPlan={handlePlanSelection}
          onLearnMore={handleLearnMore}
          selectedPlanId={selectedPlanId}
          pageVariants={basePageVariants}
          itemVariants={baseItemVariants}
          trialEligible={trialEligible}
        />

        {user?.role === "seller" && (
          <MembershipPageFooter itemVariants={baseItemVariants} />
        )}
      </motion.div>
    </>
  );
};

export default MembershipsPage;
